import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getQuizSubmissions } from '../../../../../store/dashboard/actions'
import ExportReportShared from '../../../../../components/UI/ExportReportShared/ExportReportShared'
import * as htmlToImage from 'html-to-image'
import download from 'downloadjs'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
} from 'chart.js'
import { Chart } from 'react-chartjs-2'

import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Button, Stack, Typography } from '@mui/material'
import Select from '@/components/UI/Select'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChartLegend from '../../Index/components/ChartLegend'
import { REPORTS_QUIZZES_ALL_SUBMISSIONS } from '@/config'
import dayjs from 'dayjs'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
)

export default function QuizSubmissions(props: any): ReactElement {
  const { t } = useTranslation('translation')
  const [quizSubmissions, setQuizSubmissions] = useState([])
  const dispatch = useDispatch()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [chartDownloading, setChartDownloading] = useState(false)
  const [maxValue, setMaxValue] = useState(0)
  const [dateParameters, setDateParameters] = useState({
    value: '30',
    label: t('Last 30 days'),
  })
  const [frequencyParameters, setFrequencyParameters] = useState({
    value: 'daily',
    label: t('Daily'),
  })
  const [filtersData, setFiltersData] = useState({
    filter_from_date: null,
    filter_to_date: null,
    frequency: null,
    connection_ids: null,
  })

  // Chart.js configuration
  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false, // We'll use custom legend
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: t('Date'),
          color: '#666',
        },
        ticks: {
          color: '#666',
          maxRotation: 45,
          maxTicksLimit: 8, // Limit number of ticks to avoid crowding
        },
        grid: {
          color: '#EAF0F4',
          display: false,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: t('No. of submissions'),
          color: '#666',
        },
        ticks: {
          color: '#666',
          padding: 5,
        },
        grid: {
          color: '#EAF0F4',
        },
        beginAtZero: true,
        max: maxValue ? Math.ceil(maxValue * 1.2) : undefined, // Add 20% padding above highest value
      },
    },
  }

  const getChartData = () => {
    // Debug: Log the data structure

    const labels = quizSubmissions.map((item: any, index: number) => {
      if (frequencyParameters.value === 'weekly') {
        // For weekly data, show date range
        const weekDate = item.week || item.date
        const weekStart = dayjs(weekDate)
        const weekEnd = dayjs(weekStart).add(6, 'days')
        return `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')}`
      } else {
        // For daily data, show individual date
        return dayjs(item.date).format('MMM DD')
      }
    })

    return {
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: t('Submissions'),
          data: quizSubmissions.map((item: any) => item.submission_count || 0),
          backgroundColor: 'rgba(0, 172, 255, 0.8)',
          borderColor: 'rgba(0, 172, 255, 1)',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }
  }

  useEffect(() => {
    if (
      !props.filtersData.filter_from_date ||
      !props.filtersData.filter_to_date
    ) {
      return
    }

    const fn = async () => {
      const filter = {
        ...filtersData,
        ...props.filtersData,
      }

      if (props.filtersData.dateParameters) {
        setDateParameters(props.filtersData.dateParameters)
      }

      setFiltersData(filter)
    }
    fn()
  }, [props.filtersData])

  useEffect(() => {
    if (
      dateParameters &&
      dateParameters.value &&
      dateParameters.value === 'custom'
    ) {
      // show custom date component
      return
    }

    const filter: any = {
      frequency: frequencyParameters.value,
      connection_ids: null,
    }

    setFiltersData(filter)
  }, [dateParameters, frequencyParameters])

  useEffect(() => {
    if (!filtersData.filter_from_date || !filtersData.filter_to_date) {
      return
    }
    const fun = async () => {
      setLoading(true)
      try {
        const quizSubmissionsData: any = await dispatch(
          getQuizSubmissions(filtersData),
        )
        const submissionsArray = quizSubmissionsData.data.map((d: any) => {
          return {
            ...d,
            type: 'quizsubmission',
          }
        })

        // Calculate max value for chart scaling
        const maxSubmissionCount = Math.max(
          ...submissionsArray.map((item: any) => item.submission_count || 0),
        )
        setMaxValue(maxSubmissionCount)

        setQuizSubmissions(submissionsArray)
        setLoading(false)
      } catch (error: any) {
        setQuizSubmissions([])
        setMaxValue(0)
        setLoading(false)
      }
    }
    fun()
  }, [filtersData, dispatch])

  const onDownloadChart = async () => {
    setChartDownloading(true)
    htmlToImage
      .toJpeg(document.getElementById('quiz-submissions'))
      .then(async function (dataUrl) {
        await download(dataUrl, 'quiz-submissions.png')
        setChartDownloading(false)
      })
      .catch(async (e: any) => {
        setChartDownloading(false)
        console.log('Error', e)
      })
  }

  const getWeeklyParams = () => {
    const copied = {
      ...filtersData,
    }
    delete copied.connection_ids
    return copied
  }

  return (
    <div
      className="tw-border tw-rounded-2xl tw-shadow-md tw-p-8"
      id="quiz-submissions"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        useFlexGap
        mb={2}
      >
        <Typography variant="subtitle2" component="h2">
          {t('Quiz Submissions')}
        </Typography>

        {!loading && !chartDownloading && (
          <div className="tw-flex tw-gap-2 tw-items-center tw-w-52">
            <Select
              value={frequencyParameters.value}
              options={[
                { value: 'daily', label: t('Daily') },
                { value: 'weekly', label: t('Weekly') },
              ]}
              onChange={(_value, option) => {
                setFrequencyParameters(option)
              }}
            />

            <ExportReportShared
              name={
                filtersData.frequency === 'weekly'
                  ? 'weekly-quiz-submissions'
                  : 'quiz-submissions'
              }
              filtersData={
                filtersData.frequency === 'weekly'
                  ? getWeeklyParams()
                  : filtersData
              }
              removedKeyFromObject="frequency"
              downloadChart={onDownloadChart}
            />
          </div>
        )}
      </Stack>
      {loading && (
        <div className="d-flex justify-content-center">
          <div className="spinner-border spinner-border-lg text-primary"></div>
        </div>
      )}
      {!loading && (
        <React.Fragment>
          <Stack direction="row" mb={2} pl={2} gap={2}>
            <ChartLegend
              title={t('Submissions')}
              color="rgba(0, 172, 255, 1)"
            />
          </Stack>
          <div
            className="px-4 d-flex justify-content-center align-items-center"
            style={{ width: '100%', height: '300px' }}
          >
            {quizSubmissions && quizSubmissions.length > 0 && (
              <div style={{ width: '100%', height: '300px' }}>
                <Chart
                  key={frequencyParameters.value} // Force re-render when frequency changes
                  type="bar"
                  data={getChartData()}
                  options={chartOptions}
                />
              </div>
            )}
            {quizSubmissions && quizSubmissions.length === 0 && (
              <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
                {t('There are no records to display')}
              </div>
            )}
          </div>
          <Button
            onClick={() => history.push(REPORTS_QUIZZES_ALL_SUBMISSIONS)}
            sx={{ float: 'right' }}
            endIcon={<ChevronRightIcon />}
          >
            {t('View all submissions')}
          </Button>
        </React.Fragment>
      )}
    </div>
  )
}
